import { useState } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useAuthStore } from '../store/auth.ts';
import { Icon } from './Icon.tsx';
import { NotificationBell } from './NotificationBell.tsx';

// Contextual screen title + whether this screen shows a back button (i.e. it's a
// detail/form screen rather than a bottom-nav destination).
function screenFor(pathname: string): { title: string; back: boolean } {
  if (pathname === '/dashboard' || pathname === '/') return { title: 'Documents', back: false };
  if (pathname === '/inbox') return { title: 'Inbox', back: false };
  if (pathname === '/profile') return { title: 'Profile', back: false };
  if (pathname.startsWith('/settings')) return { title: 'Settings', back: false };
  if (pathname === '/admin/orgs') return { title: 'Organizations', back: false };
  if (pathname.startsWith('/admin/orgs/')) return { title: 'Organization', back: true };
  if (pathname === '/documents/new') return { title: 'New document', back: true };
  if (pathname === '/documents/batch') return { title: 'Batch send', back: true };
  if (pathname.endsWith('/edit')) return { title: 'Prepare', back: true };
  if (pathname.startsWith('/documents/')) return { title: 'Document', back: true };
  if (pathname === '/merge') return { title: 'Merge PDFs', back: true };
  if (pathname === '/compress') return { title: 'Compress PDF', back: true };
  return { title: 'WapSign', back: false };
}

function toggleTheme(): void {
  const isDark = document.documentElement.classList.toggle('dark');
  localStorage.setItem('wapsign.theme', isDark ? 'dark' : 'light');
}

function MenuLink({ to, icon, label, onClick }: { to: string; icon: string; label: string; onClick: () => void }): JSX.Element {
  return (
    <Link to={to} onClick={onClick} className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-[0.95rem] text-foreground active:bg-muted/60">
      <Icon name={icon} size={20} className="text-muted-foreground" />
      {label}
    </Link>
  );
}

// Material 3 mobile top app bar: back/logo, contextual title, notifications +
// overflow menu (Merge/Compress/Settings/WapMeet/appearance/sign-out).
export function Header(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [menuOpen, setMenuOpen] = useState(false);
  const { title, back } = screenFor(pathname);
  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
  const close = (): void => setMenuOpen(false);

  return (
    <header
      className="md:hidden sticky top-0 z-30 bg-[hsl(var(--chrome))]"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="flex h-14 items-center gap-1 px-1.5">
        {back ? (
          <button onClick={() => window.history.back()} aria-label="Back" className="grid size-11 place-items-center rounded-full text-foreground active:bg-muted/60">
            <Icon name="arrow_back" size={24} />
          </button>
        ) : (
          <Link to="/" aria-label="Home" className="grid size-11 place-items-center">
            <img src="/icons/wapsign-mark.svg" alt="" className="size-8 rounded-[25%] object-contain" />
          </Link>
        )}

        <h1 className="flex-1 min-w-0 truncate text-[1.3rem] font-medium text-ink">{title}</h1>

        {user && (
          <>
            <NotificationBell />
            <div className="relative">
              <button onClick={() => setMenuOpen((o) => !o)} aria-label="More options" aria-expanded={menuOpen} className="grid size-11 place-items-center rounded-full text-foreground active:bg-muted/60">
                <Icon name="more_vert" size={24} />
              </button>
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={close} aria-hidden />
                  <div className="absolute right-1 top-12 z-50 w-60 rounded-2xl border border-border/50 bg-[hsl(var(--surface-lowest))] p-1.5 shadow-xl">
                    <MenuLink to="/merge" icon="merge" label="Merge PDFs" onClick={close} />
                    <MenuLink to="/compress" icon="compress" label="Compress PDF" onClick={close} />
                    {user.role === 'ORG_ADMIN' && <MenuLink to="/settings" icon="settings" label="Settings" onClick={close} />}
                    <a href="https://meetings.wapplify.app" onClick={close} className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-[0.95rem] text-foreground active:bg-muted/60">
                      <Icon name="calendar_month" size={20} className="text-muted-foreground" /> WapMeet
                    </a>
                    <button onClick={() => { toggleTheme(); close(); }} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-[0.95rem] text-foreground active:bg-muted/60">
                      <Icon name={isDark ? 'light_mode' : 'dark_mode'} size={20} className="text-muted-foreground" />
                      {isDark ? 'Light mode' : 'Dark mode'}
                    </button>
                    <div className="my-1 h-px bg-border/60" />
                    <button onClick={() => { close(); logout(); }} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-[0.95rem] text-destructive active:bg-destructive/10">
                      <Icon name="logout" size={20} /> Sign out
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </header>
  );
}
